import React, { useState, useEffect, Suspense } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  Switch,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
  Wrap,
  useDisclosure,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  HStack,
  Divider,
  Badge,
  Flex,
} from '@chakra-ui/react';
import { FaSave, FaEdit, FaTrash, FaFilePdf } from 'react-icons/fa';
import {
  FiChevronDown,
  FiArrowDown,
  FiDollarSign,
  FiUser,
  FiUsers,
  FiSettings,
  FiShare2,
  FiPrinter,
  FiWatch,
  FiArrowUp,
} from 'react-icons/fi';
import HistorialModal from 'views/Predio/HistorialdeCambios';
import HistorialPagosAP from 'views/Pagos/HistorialPagosAgua';
import CrearActualizarToma from 'views/AguaPotable/AltaToma';

interface Propietario {
  id?: string;
  idTipoContribuyente?: string;
  idTipoPropietario?: number;
  correoElectronico?  :string;
  idTipoVulnerable?: number;
  nombre: string;
  primerApellido: string;
  segundoApellido?: string;
  razonSocial?: string;
  rfc: string;
  curp?: string;
  telefono: string;
  esTitular: boolean;
  PropietarioPredio?: Array<{
    id: string;
    idClaveCatastral: string;
    idPropietario: string;
    ClaveCatastral: {
      id: string;
      claveCatastralEstandar: string;
      cuentaPredial: number;
      fechaActualizacion: string;
      usuario: string;
    };
  }>;
}

interface PropietarioDetailsProps {
  propietarioId?: string; // Ahora es opcional
}

const PropietarioDetails: React.FC<PropietarioDetailsProps> = ({
  propietarioId,
}) => {
  const [propietario, setPropietario] = useState<Propietario>({
    nombre: '',
    primerApellido: '',
    segundoApellido: '',
    razonSocial: '',
    correoElectronico:'',
    rfc: '',
    curp: '',
    telefono: '',
    esTitular: true,
    idTipoVulnerable: 1,
    idTipoPropietario: 1,
    idTipoContribuyente: 'F',
  });
  const [isEditing, setIsEditing] = useState(!propietarioId); // Activar edición si es nuevo
  const [modalKey, setModalKey] = useState(0);
  const toast = useToast();
  const {
    isOpen: isBajaOpen,
    onOpen: onBajaOpen,
    onClose: onBajaClose,
  } = useDisclosure();

  const {
    isOpen: isHistorialPagosAPOpen,
    onOpen: onHistorialPagosAPOpen,
    onClose: onHistorialPagosAPClose,
  } = useDisclosure();

  useEffect(() => {
    if (propietarioId) {
      const fetchPropietario = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/propietarios/view/${propietarioId}`,
          );
          const data = await response.json();
          console.log(data);
          setPropietario(data);
        } catch (error) {
          console.error('Error fetching propietario:', error);
          toast({
            title: 'Error',
            description: 'No se pudieron cargar los datos del propietario.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      };
      fetchPropietario();
    }
  }, [propietarioId, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPropietario((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = () => {
    setPropietario((prev) => ({ ...prev, esTitular: !prev.esTitular }));
  };

  const handleBajaOpen = () => {
    setModalKey(prev => prev + 1);
    onBajaOpen();
  };

  const handleHistorialPagosAPOpen = () => {
    setModalKey(prev => prev + 1);
    onHistorialPagosAPOpen();
  };

  const handleSave = async () => {
    try {
      const url = propietarioId
        ? `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/propietarios/propietario/${propietarioId}`
        : `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/propietarios`;

      const method = propietarioId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(propietario),
      });

      if (response.ok) {
        toast({
          title: propietarioId ? 'Actualización exitosa' : 'Creación exitosa',
          description: propietarioId
            ? 'Los datos del propietario se han actualizado.'
            : 'El nuevo propietario se ha creado.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        if (!propietarioId) {
          setPropietario({
            nombre: '',
            primerApellido: '',
            segundoApellido: '',
            razonSocial: '',
            idTipoVulnerable: 1,
            idTipoPropietario: 1,
            idTipoContribuyente: 'F',
            rfc: '',
            curp: '',
            telefono: '',
            esTitular: false,
          });
        }
        setIsEditing(false);
      } else {
        throw new Error('Error en la operación');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo guardar la información.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={6} bg="gray.50" borderRadius="xl">
      {/* Header */}
      <VStack spacing={4} align="stretch" mb={6}>
        <HStack spacing={3}>
          <Box
            p={3}
            bg="linear-gradient(135deg, #681a2d 0%, #5b182b 100%)"
            borderRadius="lg"
          >
            <FiUser size={24} color="white" />
          </Box>
          <Box flex="1">
            <Heading size="md" color="#681a2d">
              {propietarioId ? 'Detalles del Propietario' : 'Nuevo Propietario'}
            </Heading>
            <Text fontSize="sm" color="gray.600">
              {propietarioId ? 'Información completa del propietario' : 'Registra un nuevo propietario'}
            </Text>
          </Box>
          <Wrap>
            <Menu>
              <MenuButton
                as={Button}
                bg="#bb8c52"
                color="white"
                _hover={{ bg: "#a67a47" }}
                rightIcon={<FiChevronDown />}
                size="sm"
              >
                Agua Potable
              </MenuButton>
              <MenuList>
                <MenuItem icon={<FiArrowUp />} onClick={handleBajaOpen}>
                  Alta de Toma
                </MenuItem>
                <MenuItem icon={<FiDollarSign />} onClick={handleHistorialPagosAPOpen}>
                  Historial de pagos
                </MenuItem>
              </MenuList>
            </Menu>
          </Wrap>
        </HStack>
        <Box borderWidth="2px" borderColor="gray.300" borderRadius="md" />
      </VStack>

      <Modal isOpen={isBajaOpen} onClose={onBajaClose} size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            bgImage="url('/img/bg.webp')"
            bgSize="cover"
            bgPosition="center"
            bgRepeat="no-repeat"
            color="white"
            textShadow="2px 2px 4px rgba(0,0,0,0.7)"
            fontWeight="bold"
            position="relative"
          >
            Alta de Toma
          </ModalHeader>
          <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />

          <ModalBody p={0}>
            <Suspense fallback={<Spinner />}>
              <CrearActualizarToma 
                key={`toma-${modalKey}`}
                idPropietario={Number(propietarioId)}  
                alta={true} 
              />
            </Suspense>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isHistorialPagosAPOpen}
        onClose={onHistorialPagosAPClose}
        size="5xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            bgImage="url('/img/bg.webp')"
            bgSize="cover"
            bgPosition="center"
            bgRepeat="no-repeat"
            color="white"
            textShadow="2px 2px 4px rgba(0,0,0,0.7)"
            fontWeight="bold"
            position="relative"
          >
            Historial de Pagos Agua Potable
          </ModalHeader>
          <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
          <ModalBody p={0}>
            <HistorialPagosAP 
              key={`historial-${modalKey}`}
              idClaveCatastral={propietarioId} 
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Formulario */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} pt={5}>
        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Nombre</FormLabel>
          <Input
            name="nombre"
            value={propietario.nombre}
            onChange={handleInputChange}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#bb8c52" }}
            _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Primer Apellido</FormLabel>
          <Input
            name="primerApellido"
            value={propietario.primerApellido}
            onChange={handleInputChange}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#bb8c52" }}
            _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Segundo Apellido</FormLabel>
          <Input
            name="segundoApellido"
            value={propietario.segundoApellido || ''}
            onChange={handleInputChange}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#bb8c52" }}
            _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Razón Social</FormLabel>
          <Input
            name="razonSocial"
            value={propietario.razonSocial || ''}
            onChange={handleInputChange}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#bb8c52" }}
            _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">RFC</FormLabel>
          <Input
            name="rfc"
            value={propietario.rfc}
            onChange={handleInputChange}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#bb8c52" }}
            _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">CURP</FormLabel>
          <Input
            name="curp"
            value={propietario.curp || ''}
            onChange={handleInputChange}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#bb8c52" }}
            _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Tipo Contribuyente</FormLabel>
          <Input
            name="idTipoContribuyente"
            value={propietario.idTipoContribuyente}
            onChange={handleInputChange}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#bb8c52" }}
            _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Tipo Vulnerable</FormLabel>
          <Input
            name="idTipoVulnerable"
            value={propietario.idTipoVulnerable}
            onChange={handleInputChange}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#bb8c52" }}
            _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Teléfono</FormLabel>
          <Input
            name="telefono"
            value={propietario.telefono}
            onChange={handleInputChange}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#bb8c52" }}
            _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Correo Electrónico</FormLabel>
          <Input
            name="correoElectronico"
            value={propietario.correoElectronico}
            onChange={handleInputChange}
            bg="white"
            borderColor="gray.300"
            _hover={{ borderColor: "#bb8c52" }}
            _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Estado</FormLabel>
          <HStack
            spacing={3}
            p={3}
            bg="white"
            borderRadius="md"
            borderWidth="1px"
            borderColor="gray.300"
          >
            <Switch
              size="lg"
              colorScheme="green"
              isChecked={propietario.esTitular}
              onChange={handleSwitchChange}
            />
            <Text fontWeight="medium" color={propietario.esTitular ? "green.600" : "gray.500"}>
              {propietario.esTitular ? 'Es Titular' : 'No es Titular'}
            </Text>
          </HStack>
        </FormControl>
      </SimpleGrid>
      {/* Tarjetas de Claves Catastrales */}
      <Box mt={8}>
        <HStack spacing={3} mb={4}>
          <Box
            p={2}
            bg="linear-gradient(135deg, #681a2d 0%, #5b182b 100%)"
            borderRadius="md"
          >
            <FiSettings size={20} color="white" />
          </Box>
          <Heading as="h3" size="md" color="#681a2d">
            Predios Asociados
          </Heading>
          <Badge
            bg="#bb8c52"
            color="white"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="sm"
          >
            {propietario.PropietarioPredio?.length || 0} predios
          </Badge>
        </HStack>
        
        {propietario.PropietarioPredio && propietario.PropietarioPredio.length > 0 ? (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {propietario.PropietarioPredio.map((predio, index) => (
              <Box
                key={predio.idClaveCatastral}
                bg="white"
                borderRadius="lg"
                boxShadow="md"
                border="2px solid"
                borderColor="gray.200"
                p={5}
                transition="all 0.3s"
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: 'xl',
                  borderColor: '#bb8c52',
                }}
              >
                <VStack align="stretch" spacing={3}>
                  {/* Header de la tarjeta */}
                  <Flex justify="space-between" align="center">
                    <Badge
                      bg="linear-gradient(135deg, #681a2d 0%, #5b182b 100%)"
                      color="white"
                      px={3}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      🏠 PREDIO #{index + 1}
                    </Badge>
                  </Flex>

                  <Divider borderColor="gray.300" />

                  {/* Clave Catastral */}
                  <Box>
                    <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>
                      Clave Catastral
                    </Text>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color="#681a2d"
                      bg="#f0e6d2"
                      px={3}
                      py={2}
                      borderRadius="md"
                      textAlign="center"
                    >
                      {predio.ClaveCatastral.claveCatastralEstandar}
                    </Text>
                  </Box>

                  {/* Cuenta Predial */}
                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                      Cuenta Predial
                    </Text>
                    <Badge
                      bg="#bb8c52"
                      color="white"
                      px={3}
                      py={1}
                      borderRadius="md"
                      fontSize="sm"
                    >
                      {predio.ClaveCatastral.cuentaPredial}
                    </Badge>
                  </Flex>

                  {/* Fecha de Actualización */}
                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                      📅 Última Actualización
                    </Text>
                    <Text fontSize="sm" color="gray.700" fontWeight="medium">
                      {new Date(predio.ClaveCatastral.fechaActualizacion).toLocaleDateString('es-MX', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </Text>
                  </Flex>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        ) : (
          <Box
            bg="white"
            borderRadius="lg"
            p={8}
            textAlign="center"
            border="2px dashed"
            borderColor="gray.300"
          >
            <Text color="gray.500" fontSize="md">
              No hay predios asociados a este propietario
            </Text>
          </Box>
        )}
      </Box>

      {/* Footer con botón */}
      <Box borderWidth="2px" borderColor="gray.300" borderRadius="md" my={6} />
      <HStack spacing={4} justify="flex-end">
        <Button
          bg="linear-gradient(135deg, #681a2d 0%, #5b182b 100%)"
          color="white"
          _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
          _active={{ transform: "translateY(0)" }}
          transition="all 0.2s"
          onClick={handleSave}
          px={8}
        >
          {propietarioId ? 'Guardar Cambios' : 'Crear Propietario'}
        </Button>
      </HStack>
    </Box>
  );
};

export default PropietarioDetails;
